/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import EditIcon from "@material-ui/icons/EditOutlined";
import ErrorIcon from "@material-ui/icons/Error";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import SmsIcon from "@material-ui/icons/SmsOutlined";
import { useI18n } from "hooks/i18n/i18n";
import { useId, useState } from "react";

import { PhoneNumberField } from "./PhoneNumberField";
import {
  VERIFICATION_CODE_LENGTH,
  VerificationCodeInput,
} from "./VerificationCodeInput";
import { formatPhoneNumber, maskPhoneNumber } from "./formatPhoneNumber";
import { useLinkPhoneNumber, LinkPhoneNumberError } from "./useLinkPhoneNumber";
import { useRemainingSecondsToResend } from "./useRemainingSecondsToResend";

import type { LastSentCode } from "./useLinkPhoneNumber";
import type { FC } from "react";

const ERROR_MESSAGE_KEY = {
  invalidPhoneNumber: "phoneNumber.errorInvalidPhoneNumber",
  invalidCode: "phoneNumber.errorInvalidCode",
  codeExpired: "phoneNumber.errorCodeExpired",
  alreadyInUse: "phoneNumber.errorAlreadyInUse",
  alreadyLinked: "phoneNumber.errorAlreadyLinked",
  tooManyRequests: "phoneNumber.errorTooManyRequests",
  recaptchaFailed: "phoneNumber.errorRecaptchaFailed",
  requiresRecentLogin: "phoneNumber.errorRequiresRecentLogin",
  unavailable: "phoneNumber.errorUnavailable",
  unknown: "phoneNumber.errorUnknown",
} as const satisfies Record<LinkPhoneNumberError, string>;

type Props = {
  /**
   * このフォームが描画すると、フォームを含むツリーが再描画で差し替わったときに
   * 要素ごと消え、チャレンジ中だった reCAPTCHA が描画先を失って壊れる。
   * 画面の寿命と同じだけ存在する位置に置くこと。
   * 同時に複数描画すると壊れるため、同じ画面に2つ置く場合は別の id にする。
   */
  recaptchaContainerId: string;
  description?: string;
  currentPhoneNumber?: string;
  onCancel?: () => void;
  onVerified: () => void;
};

export const PhoneNumberVerificationForm: FC<Props> = ({
  recaptchaContainerId,
  description,
  currentPhoneNumber,
  onCancel,
  onVerified,
}) => {
  const classes = styles();

  const {
    verificationState,
    error,
    isSubmitting,
    lastSentCode,
    requestVerificationCode,
    confirmVerificationCode,
    backToInput,
  } = useLinkPhoneNumber(recaptchaContainerId);

  const sentPhoneNumber =
    verificationState.step === "codeSent"
      ? verificationState.sentPhoneNumber
      : "";

  return (
    <div css={classes.card}>
      {verificationState.step === "input" ? (
        <PhoneNumberInputForm
          description={description}
          currentPhoneNumber={currentPhoneNumber}
          error={error}
          isSubmitting={isSubmitting}
          lastSentCode={lastSentCode}
          onSubmit={(phoneNumber) => {
            void requestVerificationCode(phoneNumber);
          }}
          onCancel={onCancel}
        />
      ) : (
        <VerificationCodeForm
          sentPhoneNumber={sentPhoneNumber}
          error={error}
          isSubmitting={isSubmitting}
          lastSentCode={lastSentCode}
          onSubmit={(verificationCode) => {
            void confirmVerificationCode(verificationCode).then(
              (isVerified) => {
                if (isVerified) {
                  onVerified();
                }
              }
            );
          }}
          onResend={() => {
            void requestVerificationCode(sentPhoneNumber);
          }}
          onBackToInput={backToInput}
        />
      )}
    </div>
  );
};

type PhoneNumberInputFormProps = {
  description?: string;
  currentPhoneNumber?: string;
  error: LinkPhoneNumberError | undefined;
  isSubmitting: boolean;
  lastSentCode: LastSentCode | undefined;
  onSubmit: (phoneNumber: string) => void;
  onCancel?: () => void;
};

const PhoneNumberInputForm: FC<PhoneNumberInputFormProps> = ({
  description,
  currentPhoneNumber,
  error,
  isSubmitting,
  lastSentCode,
  onSubmit,
  onCancel,
}) => {
  const { t } = useI18n();
  const classes = styles();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const [isPhoneNumberTouched, setIsPhoneNumberTouched] = useState(false);

  const remainingSecondsToResend = useRemainingSecondsToResend(
    lastSentCode?.sentAt
  );
  const isResendBlocked =
    remainingSecondsToResend > 0 && lastSentCode?.phoneNumber === phoneNumber;
  const isSendDisabled = isSubmitting || isResendBlocked;
  // 新規登録と番号の変更で違うのはここだけ
  const formVariant =
    currentPhoneNumber === undefined
      ? {
          statusIcon: <ErrorOutlineIcon css={classes.statusIcon} />,
          statusText: t("phoneNumber.statusUnverified"),
          lead: description && <p css={classes.description}>{description}</p>,
          notes: [
            t("phoneNumber.annotationOnePerAccount"),
            t("phoneNumber.annotationSms"),
          ],
          inputLabel: t("phoneNumber.inputLabel"),
          sendButtonStyle: undefined,
          cancelButton: null,
        }
      : {
          statusIcon: <EditIcon css={classes.statusIcon} />,
          statusText: t("phoneNumber.statusChanging"),
          lead: (
            <div css={classes.current}>
              <p css={classes.currentLabel}>{t("phoneNumber.currentNumber")}</p>
              <p css={classes.currentNumber}>
                {maskPhoneNumber(formatPhoneNumber(currentPhoneNumber))}
              </p>
            </div>
          ),
          notes: [
            t("phoneNumber.annotationCurrentStaysValid"),
            t("phoneNumber.annotationOnePerAccount"),
          ],
          inputLabel: t("phoneNumber.newInputLabel"),
          sendButtonStyle: classes.narrowButton,
          cancelButton:
            onCancel === undefined ? null : (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={onCancel}
                css={[
                  classes.button,
                  classes.narrowButton,
                  classes.outlineButton,
                ]}
              >
                {t("phoneNumber.cancel")}
              </button>
            ),
        };

  return (
    <form
      css={classes.body}
      onSubmit={(e) => {
        e.preventDefault();
        if (isSendDisabled) return;
        if (!isPhoneNumberValid) {
          setIsPhoneNumberTouched(true);
          return;
        }
        onSubmit(phoneNumber);
      }}
    >
      <p css={classes.status}>
        {formVariant.statusIcon}
        <span>{formVariant.statusText}</span>
      </p>
      {formVariant.lead}
      <div css={classes.notes}>
        {formVariant.notes.map((note) => (
          <p key={note}>{note}</p>
        ))}
      </div>
      <hr css={classes.divider} />
      {error !== undefined && (
        <p css={classes.fieldError} role="alert">
          <ErrorIcon css={classes.fieldErrorIcon} />
          <span>{t(ERROR_MESSAGE_KEY[error])}</span>
        </p>
      )}
      <PhoneNumberField
        label={formVariant.inputLabel}
        onChangeNumber={setPhoneNumber}
        onChangeValidity={setIsPhoneNumberValid}
        onBlur={() => setIsPhoneNumberTouched(true)}
        showError={isPhoneNumberTouched && !isPhoneNumberValid}
        disabled={isSubmitting}
      />
      {isResendBlocked && (
        <p css={classes.resend}>
          {t("phoneNumber.resendCountdown", {
            seconds: remainingSecondsToResend,
          })}
        </p>
      )}
      <div css={classes.actions}>
        <button
          type="submit"
          disabled={isSendDisabled}
          css={[
            classes.button,
            formVariant.sendButtonStyle,
            isSendDisabled && classes.disabledButton,
          ]}
        >
          {t("phoneNumber.sendCode")}
        </button>
        {formVariant.cancelButton}
      </div>
    </form>
  );
};

type VerificationCodeFormProps = {
  sentPhoneNumber: string;
  error: LinkPhoneNumberError | undefined;
  isSubmitting: boolean;
  lastSentCode: LastSentCode | undefined;
  onSubmit: (verificationCode: string) => void;
  onResend: () => void;
  onBackToInput: () => void;
};

const VerificationCodeForm: FC<VerificationCodeFormProps> = ({
  sentPhoneNumber,
  error,
  isSubmitting,
  lastSentCode,
  onSubmit,
  onResend,
  onBackToInput,
}) => {
  const { t } = useI18n();
  const classes = styles();
  const verificationCodeLabelId = useId();
  const verificationCodeErrorId = useId();

  const [codeInput, setCodeInput] = useState("");

  const remainingSecondsToResend = useRemainingSecondsToResend(
    lastSentCode?.sentAt
  );
  const isCodeError = error === "invalidCode" || error === "codeExpired";
  const isVerifyDisabled =
    isSubmitting || codeInput.length !== VERIFICATION_CODE_LENGTH;

  return (
    <form
      css={classes.body}
      onSubmit={(e) => {
        e.preventDefault();
        if (isVerifyDisabled) return;
        onSubmit(codeInput);
      }}
    >
      <p css={classes.status}>
        <SmsIcon css={[classes.statusIcon, classes.statusIconSent]} />
        <span>{t("phoneNumber.statusCodeSent")}</span>
      </p>
      <div css={classes.sentTo}>
        {/* この番号はまだ登録されていないため、登録済みのラベルは付けない */}
        <p css={classes.description}>
          {t("phoneNumber.codeSent", {
            phoneNumber: formatPhoneNumber(sentPhoneNumber),
          })}
        </p>
        <button
          type="button"
          disabled={isSubmitting}
          onClick={onBackToInput}
          css={classes.textButton}
        >
          {t("phoneNumber.resend")}
        </button>
      </div>
      <hr css={classes.divider} />
      <div css={classes.codeField}>
        <p css={classes.label} id={verificationCodeLabelId}>
          {t("phoneNumber.codeInputLabel")}
        </p>
        <VerificationCodeInput
          labelId={verificationCodeLabelId}
          errorId={error === undefined ? undefined : verificationCodeErrorId}
          value={codeInput}
          onChange={setCodeInput}
          disabled={isSubmitting}
          isError={isCodeError}
        />
        {error !== undefined && (
          <p css={classes.fieldError} id={verificationCodeErrorId} role="alert">
            <ErrorIcon css={classes.fieldErrorIcon} />
            <span>{t(ERROR_MESSAGE_KEY[error])}</span>
          </p>
        )}
        <p css={classes.resend}>
          <span>{t("phoneNumber.resendGuide")}</span>
          {remainingSecondsToResend > 0 ? (
            <span css={classes.resendWaiting}>
              {t("phoneNumber.resendCountdown", {
                seconds: remainingSecondsToResend,
              })}
            </span>
          ) : (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                setCodeInput("");
                onResend();
              }}
              css={classes.textButton}
            >
              {t("phoneNumber.resendCode")}
            </button>
          )}
        </p>
      </div>
      <div css={classes.actions}>
        <button
          type="submit"
          disabled={isVerifyDisabled}
          css={[classes.button, isVerifyDisabled && classes.disabledButton]}
        >
          {t("phoneNumber.verify")}
        </button>
      </div>
    </form>
  );
};

const styles = () => {
  return {
    card: css({
      width: "100%",
      maxWidth: "740px",
      margin: "0 auto",
      padding: "16px",
      backgroundColor: "#fff",
      border: "2px solid #dcdbdc",
      "@media screen and (min-width: 768px)": {
        padding: "24px",
        borderWidth: "1px",
      },
    }),
    body: css({
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      "@media screen and (min-width: 768px)": {
        gap: "20px",
      },
    }),
    status: css({
      display: "flex",
      margin: 0,
      gap: "10px",
      alignItems: "center",
      fontSize: "16px",
      fontWeight: "bold",
      lineHeight: "22px",
      "@media screen and (min-width: 768px)": {
        fontSize: "18px",
        lineHeight: "25px",
      },
    }),
    statusIcon: css({
      flex: "none",
      fontSize: "24px",
      color: "#6f6f6f",
    }),
    statusIconSent: css({
      color: "#00c2ae",
    }),
    description: css({
      margin: 0,
      fontSize: "14px",
      lineHeight: "22px",
    }),
    notes: css({
      display: "flex",
      p: {
        margin: 0,
      },
      flexDirection: "column",
      gap: "4px",
      color: "#6f6f6f",
      fontSize: "12px",
      lineHeight: "18px",
      "@media screen and (min-width: 768px)": {
        fontSize: "13px",
        lineHeight: "20px",
      },
    }),
    sentTo: css({
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "6px",
    }),
    divider: css({
      height: "1px",
      margin: 0,
      backgroundColor: "#e5e5e5",
      border: "none",
    }),
    codeField: css({
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    }),
    label: css({
      margin: 0,
      fontSize: "15px",
      lineHeight: "20px",
      fontWeight: 500,
    }),
    fieldError: css({
      display: "flex",
      margin: 0,
      gap: "6px",
      alignItems: "flex-start",
      color: "#e53935",
      fontSize: "13px",
      fontWeight: "bold",
      lineHeight: "20px",
    }),
    fieldErrorIcon: css({
      flex: "none",
      fontSize: "16px",
      marginTop: "2px",
    }),
    resend: css({
      display: "flex",
      margin: 0,
      flexWrap: "wrap",
      alignItems: "center",
      gap: "0 4px",
      paddingTop: "4px",
      color: "#6f6f6f",
      fontSize: "13px",
      lineHeight: "20px",
    }),
    current: css({
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    }),
    currentLabel: css({
      margin: 0,
      color: "#6f6f6f",
      fontSize: "12px",
      lineHeight: "18px",
      "@media screen and (min-width: 768px)": {
        fontSize: "13px",
        lineHeight: "20px",
      },
    }),
    currentNumber: css({
      margin: 0,
      fontSize: "18px",
      fontWeight: "bold",
      lineHeight: "26px",
    }),
    actions: css({
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      alignItems: "center",
      "@media screen and (min-width: 768px)": {
        flexDirection: "row",
        justifyContent: "center",
      },
    }),
    button: css({
      width: "100%",
      minHeight: "44px",
      padding: "10px 16px",
      color: "#fff",
      fontFamily: "inherit",
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: "20px",
      backgroundColor: "#00c2ae",
      border: "none",
      cursor: "pointer",
      "@media screen and (min-width: 768px)": {
        maxWidth: "320px",
        minHeight: 0,
      },
    }),
    narrowButton: css({
      "@media screen and (min-width: 768px)": {
        maxWidth: "240px",
      },
    }),
    outlineButton: css({
      color: "#0b0b0b",
      backgroundColor: "#fff",
      border: "1px solid #0b0b0b",
    }),
    disabledButton: css({
      backgroundColor: "#d4d4d4",
      cursor: "default",
    }),
    resendWaiting: css({
      color: "#9e9e9e",
      fontWeight: "bold",
    }),
    textButton: css({
      padding: 0,
      color: "#00c2ae",
      fontFamily: "inherit",
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: "20px",
      textDecoration: "underline",
      backgroundColor: "transparent",
      border: "none",
      cursor: "pointer",
    }),
  };
};
